import React, { useState, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { aboutData as mockAboutData } from '../../data/mock';
import { useLanguage } from '../../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AboutSection = () => {
  const [aboutData, setAboutData] = useState(null);
  const [translatedData, setTranslatedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [translating, setTranslating] = useState(false);
  const { t, currentLanguage } = useLanguage();

  // Fetch about data
  useEffect(() => {
    const fetchAbout = async () => {
      try {
        const response = await fetch(`${API_URL}/api/about`);
        if (response.ok) {
          const data = await response.json();
          setAboutData(data);
        }
      } catch (error) {
        console.error('Error fetching about:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAbout();
  }, []);

  // Translate function
  const translateTexts = useCallback(async (texts, targetLang) => {
    try {
      const response = await fetch(`${API_URL}/api/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          texts,
          target_language: targetLang,
          source_language: 'pt'
        })
      });

      if (response.ok) {
        const data = await response.json();
        return data.translations;
      }
    } catch (error) {
      console.error('Translation error:', error);
    }
    return texts;
  }, []);

  // Translate content when language changes
  useEffect(() => {
    const translateAboutContent = async () => {
      const data = aboutData || mockAboutData;
      
      console.log('AboutSection: Language changed to', currentLanguage);
      
      // If Portuguese, just use original data
      if (currentLanguage === 'pt') {
        setTranslatedData(data);
        return;
      }

      setTranslating(true);
      
      try {
        // Collect all texts to translate
        const textsToTranslate = [
          ...data.paragraphs,
          ...data.values.map(v => v.title),
          ...data.values.map(v => v.description)
        ];
        
        console.log('AboutSection: Translating', textsToTranslate.length, 'texts');
        
        const translations = await translateTexts(textsToTranslate, currentLanguage);
        
        console.log('AboutSection: Got translations', translations?.length);
        
        if (translations && translations.length > 0) {
          const numParagraphs = data.paragraphs.length;
          const numValues = data.values.length;
          
          const newData = {
            ...data,
            paragraphs: translations.slice(0, numParagraphs),
            values: data.values.map((v, i) => ({
              title: translations[numParagraphs + i] || v.title,
              description: translations[numParagraphs + numValues + i] || v.description
            }))
          };
          
          console.log('AboutSection: Setting translated data', newData.paragraphs[0]?.substring(0, 50));
          setTranslatedData(newData);
        } else {
          console.log('AboutSection: No translations received');
          setTranslatedData(data);
        }
      } catch (error) {
        console.error('Translation error:', error);
        setTranslatedData(data);
      } finally {
        setTranslating(false);
      }
    };

    if (!loading) {
      translateAboutContent();
    }
  }, [aboutData, currentLanguage, loading, translateTexts]);

  // Use translated data, API data, or fallback to mock data
  const displayData = translatedData || aboutData || mockAboutData;

  if (loading) {
    return (
      <section id="about" className="section-padding bg-transparent">
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#C4B5A0]" />
        </div>
      </section>
    );
  }

  return (
    <section id="about" className="section-padding bg-transparent">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image Column */}
          <div className="relative animate-fade-in-up flex justify-center lg:justify-start">
            {/* Main Photo Container */}
            <div className="relative">
              {/* Decorative Frame */}
              <div className="absolute -inset-4 bg-gradient-to-br from-[#D4C4B0]/40 to-[#B5C4B1]/30 rounded-2xl -z-10" />
              
              {/* Photo */}
              <div className="relative w-[320px] md:w-[380px] aspect-[3/4] rounded-xl overflow-hidden shadow-2xl border-4 border-white/80">
                <img
                  src={displayData.image}
                  alt="Véronique de Sousa Rendeiro - Wedding Planner"
                  className="img-cover hover:scale-105 transition-transform duration-700"
                />
                
                {/* Elegant Overlay Gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#2C2C2C]/20 via-transparent to-transparent" />
              </div>
              
              {/* Decorative Elements */}
              <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-[#D4C4B0]/40 rounded-full blur-2xl" />
              <div className="absolute -top-6 -left-6 w-20 h-20 bg-[#B5C4B1]/30 rounded-full blur-2xl" />
              
              {/* Floating Badge */}
              <div className="absolute -bottom-4 -right-4 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg">
                <p className="text-xs text-[#8B7D6B] uppercase tracking-wider">{t('about.badge')}</p>
                <p className="font-['Cormorant_Garamond'] text-lg text-[#2C2C2C]">Véronique</p>
              </div>
            </div>
          </div>
          
          {/* Content Column */}
          <div className="lg:pl-8">
            {/* Section Label */}
            <p className="text-caption text-[#C4B5A0] mb-4">
              {t('about.label')}
            </p>
            
            {/* Title */}
            <h2 className="heading-secondary text-4xl md:text-5xl text-[#2C2C2C] mb-6">
              {t('about.title')}
            </h2>
            
            <div className="divider !mx-0 !ml-0" />
            
            {/* Paragraphs */}
            <div className={`space-y-5 mb-10 transition-opacity duration-300 ${translating ? 'opacity-50' : 'opacity-100'}`}>
              {displayData.paragraphs.map((paragraph, index) => (
                <p key={index} className="text-body text-[#6B4E37]">
                  {paragraph}
                </p>
              ))}
            </div>
            
            {/* Values */}
            <div className={`grid grid-cols-1 sm:grid-cols-3 gap-6 transition-opacity duration-300 ${translating ? 'opacity-50' : 'opacity-100'}`}>
              {displayData.values.map((value, index) => (
                <div
                  key={index}
                  className="text-center sm:text-left p-4 bg-white/60 backdrop-blur-sm rounded-lg shadow-sm"
                >
                  <h4 className="font-['Cormorant_Garamond'] text-xl text-[#2C2C2C] mb-2">
                    {value.title}
                  </h4>
                  <p className="text-sm text-[#8B7D6B]">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
